import React, { useState } from 'react';
import { Database, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

/**
 * PushToSupabaseButton - Reusable deployment button component
 * 
 * Usage:
 * <PushToSupabaseButton 
 *   label="Deploy Migration" 
 *   action="migration"
 *   onSuccess={() => console.log('Deployed!')}
 * />
 */
const PushToSupabaseButton = ({ 
  label = "Push to Supabase", 
  action = "deploy",
  variant = "primary",
  onSuccess,
  onError 
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [message, setMessage] = useState('');

  const handleDeploy = async () => {
    setLoading(true);
    setStatus(null);
    setMessage('');

    try {
      // Simulate deployment action
      // In a real implementation, this would call your deployment API
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check Supabase connection
      const { data, error } = await supabase?.from('user_profiles')?.select('count')?.limit(1);
      
      if (error) throw error;

      setStatus('success');
      setMessage(`${label} completed successfully!`);
      
      if (onSuccess) onSuccess();
    } catch (error) {
      setStatus('error');
      setMessage(error?.message || 'Deployment failed. Please try again.');
      
      if (onError) onError(error);
    } finally {
      setLoading(false);
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setStatus(null);
        setMessage('');
      }, 5000);
    }
  };

  const buttonVariants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white'
  };

  const buttonClass = buttonVariants?.[variant] || buttonVariants?.primary;

  return (
    <div className="space-y-2">
      <button
        onClick={handleDeploy}
        disabled={loading}
        className={`
          ${buttonClass}
          px-6 py-3 rounded-lg font-semibold 
          transition-all duration-200 
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center gap-2 justify-center
          min-w-[200px]
          shadow-lg hover:shadow-xl
        `}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Deploying...</span>
          </>
        ) : (
          <>
            <Database className="w-5 h-5" />
            <span>{label}</span>
          </>
        )}
      </button>

      {/* Status Messages */}
      {status === 'success' && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{message}</span>
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{message}</span>
        </div>
      )}
    </div>
  );
};

export default PushToSupabaseButton;